import React from 'react';
import LegalLayout, {
  LegalSection as Section,
  LegalSubSection as SubSection,
} from '../components/layout/LegalLayout';

const LAST_UPDATED = 'July 4, 2026';

export default function PrivacyPage() {
  return (
    <LegalLayout
      title="Privacy Policy"
      lastUpdated={LAST_UPDATED}
      back={{ to: '/register', label: 'Back to Registration' }}
    >
        <Section title="1. Who We Are">
          <p>
            MortisEngine ("we", "us", "our") operates the MortisEngine website and desktop
            loader application. For privacy-related inquiries or to exercise your rights,
            contact us via our Discord server at{' '}
            <a href="https://discord.gg/Frbjdmtbrt" target="_blank" rel="noopener noreferrer"
              className="text-brand-400 hover:text-brand-300 underline">
              discord.gg/Frbjdmtbrt
            </a>.
          </p>
          <p>
            This policy applies to all personal data we process through the website
            (mortisengine.com) and the MortisEngine desktop loader application.
          </p>
        </Section>

        <Section title="2. Data We Collect and Why">
          <p className="mb-3">
            We collect the minimum data necessary to provide the Service. Below is a full
            breakdown of what we collect, why, and the legal basis under GDPR.
          </p>

          <SubSection title="2.1 Account Data">
            <DataTable rows={[
              ['Username', 'Account identification and login', 'Contract'],
              ['Email address', 'Account identification, communication', 'Contract'],
              ['Password (bcrypt hash)', 'Authentication — plaintext is never stored', 'Contract'],
              ['Registration date', 'Account management', 'Contract'],
              ['Terms of Service acceptance timestamp', 'Proof of consent', 'Legal obligation'],
            ]} />
          </SubSection>

          <SubSection title="2.2 Hardware & Security Data">
            <p className="text-gray-400 mb-3">
              The desktop loader application collects the following data to enforce license
              binding and prevent abuse. You are informed of this collection at registration
              and by using the loader.
            </p>
            <DataTable rows={[
              ['Hardware ID (HWID)', 'Derived from CPU, motherboard, and OS. Used to bind your license to a single device and prevent sharing.', 'Legitimate interest'],
              ['Running process list', 'Collected by the loader on injection to detect prohibited software (debuggers, cheating tools, or known exploit programs). Stored server-side when a ban is triggered.', 'Legitimate interest'],
              ['Loader version', 'Checked on login to enforce minimum version requirements.', 'Legitimate interest'],
            ]} />
          </SubSection>

          <SubSection title="2.3 Network & Log Data">
            <DataTable rows={[
              ['IP address', 'Stored in security and audit logs. Used to detect account sharing, ban evasion, and abuse.', 'Legitimate interest'],
              ['Audit log entries', 'Actions taken on your account (login, key activation, password change, etc.) with timestamp and IP.', 'Legitimate interest'],
              ['Ban log entries', 'If your account is banned, the reason, HWID, running process list, IP, and User-Agent are recorded.', 'Legitimate interest'],
            ]} />
          </SubSection>

          <SubSection title="2.4 Purchase & Subscription Data">
            <DataTable rows={[
              ['Order history', 'Plan, price, payment method, timestamps', 'Contract'],
              ['Litecoin address', 'Generated per order for crypto payment processing', 'Contract'],
              ['License key', 'Generated and assigned upon successful payment', 'Contract'],
              ['Subscription status and expiry', 'Checked by the loader on every login', 'Contract'],
            ]} />
          </SubSection>

          <SubSection title="2.5 Invite System Data">
            <DataTable rows={[
              ['Invite request reason', 'Text submitted when requesting an invite code', 'Legitimate interest'],
              ['Invite code usage', 'Who generated and who used each invite code', 'Legitimate interest'],
            ]} />
          </SubSection>
        </Section>

        <Section title="3. Data Retention">
          <p className="mb-3">We retain data only as long as necessary for the stated purpose.</p>
          <DataTable rows={[
            ['Account data', 'Until account deletion is requested, or account is permanently banned'],
            ['Audit logs & IP addresses', 'Retained for the lifetime of the account. Audit logs record actions such as logins, key activations, and password changes. We retain these to detect and prevent policy violations including the creation of multiple accounts, ban evasion, and other forms of abuse. This constitutes a legitimate interest under GDPR Art. 6(1)(f).'],
            ['Ban logs & process lists', 'Retained indefinitely while the ban is active. If a ban is reversed, associated logs are deleted upon request.'],
            ['Order & payment records', '5 years from the transaction date (legal/financial obligation)'],
            ['Refresh tokens', '7 days from issuance; deleted immediately on logout'],
            ['Running process list (ban context)', 'Stored with the ban log entry; subject to the same retention as ban logs above'],
          ]} />
        </Section>

        <Section title="4. Third-Party Data Transfers">
          <SubSection title="4.1 Discord (International Transfer)">
            <p>
              We operate a logging bot connected to our private Discord server. The following
              events trigger an automatic notification to our internal Discord channel:
            </p>
            <ul className="list-disc list-inside space-y-1.5 mt-2 ml-1 text-gray-400">
              <li><strong className="text-white">Registration</strong> — username, email, IP address, whether an invite was used</li>
              <li><strong className="text-white">Key activation</strong> — username, activated key, subscription duration, IP</li>
              <li><strong className="text-white">Ban (automated)</strong> — username, HWID, ban reason, IP, number of flagged processes</li>
              <li><strong className="text-white">HWID mismatch / conflict</strong> — username, HWID, IP</li>
            </ul>
            <p className="mt-3">
              Discord Inc. is a US-based company. Data sent to Discord is transferred outside
              the European Economic Area. Discord processes this data under Standard
              Contractual Clauses (SCCs) for EU–US transfers, as described in their{' '}
              <a href="https://discord.com/privacy" target="_blank" rel="noopener noreferrer"
                className="text-brand-400 hover:text-brand-300 underline">
                Privacy Policy
              </a>.
              Data sent to Discord is visible only to our staff and is not shared further.
            </p>
          </SubSection>

          <SubSection title="4.2 Litecoin Blockchain">
            <p>
              Crypto payments are processed via the Litecoin network. Payment transactions
              (wallet addresses and amounts) are recorded on a public, immutable blockchain.
              We have no control over data on the blockchain and it cannot be deleted,
              including upon a request to exercise your right to erasure.
            </p>
          </SubSection>

          <SubSection title="4.3 No Other Third Parties">
            <p>
              We do not sell, rent, or share your personal data with any advertising networks,
              data brokers, or other third parties beyond those listed above.
            </p>
          </SubSection>
        </Section>

        <Section title="5. Cookies and Local Storage">
          <p>
            We do not use tracking cookies or third-party analytics. We store a login session
            in your browser to keep you signed in between visits. This session expires
            automatically after 7 days of inactivity and is cleared immediately on logout.
          </p>
          <p>
            No advertising or tracking scripts are loaded on this website.
          </p>
        </Section>

        <Section title="6. Your Rights Under GDPR">
          <p className="mb-3">
            If you are located in the European Economic Area, you have the following rights
            regarding your personal data:
          </p>
          <ul className="space-y-3">
            {[
              ['Right of access', 'You can request a copy of the personal data we hold about you.'],
              ['Right to rectification', 'You can request correction of inaccurate data (e.g. email address).'],
              ['Right to erasure', 'You can request deletion of your account and associated personal data. Note: ban logs may be retained if there is a legitimate security interest, and order records are kept for the legally required period. Data already recorded on the Litecoin blockchain cannot be deleted.'],
              ['Right to restriction of processing', 'You can request that we restrict the use of your data — for example, while a dispute is being resolved — without deleting it.'],
              ['Right to data portability', 'You can request your account data in a machine-readable format.'],
              ['Right to object', 'You can object to processing based on legitimate interest (e.g. audit logging, security monitoring). We will assess whether our legitimate interest overrides your objection.'],
              ['Right to lodge a complaint', 'You have the right to lodge a complaint with your local data protection supervisory authority.'],
            ].map(([right, desc]) => (
              <li key={right} className="flex items-start gap-3">
                <span className="text-brand-400 mt-0.5 flex-shrink-0">→</span>
                <div>
                  <span className="text-white font-semibold">{right}: </span>
                  <span className="text-gray-400">{desc}</span>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-4">
            To exercise any of these rights, contact us via Discord. We will respond within
            30 days. We may ask you to verify your identity before processing the request.
          </p>
        </Section>

        <Section title="7. Automated Decision-Making">
          <p>
            The MortisEngine desktop loader includes an automated security system that monitors
            for activity indicating abuse, tampering, or attempts to circumvent licensing
            protections — such as the presence of debuggers or known exploit tools. If such
            activity is detected, your account may be <strong className="text-white">automatically
            banned without prior warning and without human review</strong> at the time of the action.
          </p>
          <p>
            This constitutes automated decision-making with significant effect under
            GDPR Art. 22. The automated system acts on the following signals:
          </p>
          <ul className="list-disc list-inside space-y-1.5 mt-2 ml-1 text-gray-400">
            <li>Detection of a debugger attached to the loader process</li>
            <li>Presence of processes associated with known exploit or analysis tools</li>
          </ul>
          <p className="mt-3">
            You have the right to request human review of any automated ban decision by
            opening a support ticket on our Discord server. We are not obligated to reverse
            the decision, but every request will be reviewed by a staff member.
          </p>
        </Section>

        <Section title="8. Data Breach Notification">
          <p>
            In the event of a personal data breach that is likely to result in a risk to your
            rights and freedoms, we will notify affected users without undue delay and, where
            feasible, within 72 hours of becoming aware of the breach. Notification will be
            made via the email address associated with your account. We will also notify the
            relevant supervisory authority as required by law.
          </p>
        </Section>

        <Section title="9. Data Security">
          <p>
            Passwords are stored as cryptographic hashes — plaintext passwords are never
            stored or transmitted. Authentication uses short-lived tokens with automatic
            expiry and rotation. The cheat payload is encrypted on a per-user basis before
            transmission, using keys derived from your hardware identity.
          </p>
          <p>
            Despite these measures, no system is 100% secure. You are responsible for
            keeping your account credentials confidential.
          </p>
        </Section>

        <Section title="10. Children">
          <p>
            The Service is not directed at individuals under the age of 16. We do not
            knowingly collect personal data from children. If you believe a minor has
            registered, contact us and we will delete the account.
          </p>
        </Section>

        <Section title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time. We will update the "Last
            updated" date at the top of this page. Continued use of the Service after changes
            constitutes acceptance of the revised policy. We encourage you to review this
            page periodically.
          </p>
        </Section>

        <Section title="12. Contact">
          <p>
            For any privacy-related questions, data requests, or to exercise your rights,
            reach out via our Discord server:{' '}
            <a href="https://discord.gg/Frbjdmtbrt" target="_blank" rel="noopener noreferrer"
              className="text-brand-400 hover:text-brand-300 underline">
              discord.gg/Frbjdmtbrt
            </a>.
          </p>
        </Section>
    </LegalLayout>
  );
}

function DataTable({ rows }) {
  return (
    <div className="surface-sunken overflow-hidden scroll-x">
      <table className="w-full text-xs">
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-white/[0.05] last:border-0">
              <td className="px-4 py-3 text-white font-medium align-top pr-6 w-48 min-w-[9rem]">
                {row[0]}
              </td>
              <td className="px-4 py-3 text-gray-400 align-top leading-relaxed">{row[1]}</td>
              {row[2] && (
                <td className="px-4 py-3 align-top whitespace-nowrap">
                  <span className="badge-brand">{row[2]}</span>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
